import React, { useCallback, useMemo, useState } from 'react'
import type { ViewMode, ToastMessage } from '../../types'
import { CockpitRuntime } from './CockpitRuntime'
import { compileViews } from '../../lib/cockpit/compiler/viewCompiler'
import { DEFAULT_PERSONAS, type CIG } from '../../lib/cockpit/schemas'
import { normalizeLegacyMetrics } from '../../lib/cockpit/normalizers'
import {
  dispatchPodcastPlayback,
  getPodcastActionState,
  resolveIntelligencePodcastPlayback,
} from '../../lib/podcastPlayback'
import {
  hydratePodcastSurfaceState,
  type PodcastReportPayload,
  type PodcastSurfaceState,
} from '../../lib/podcastHydration'

export interface DeepResearchData {
  text?: string
  citations?: any[]
  sources?: any[]
  status?: string
  provider?: string
}

export interface IntelligenceReportData extends PodcastReportPayload {
  cig: CIG
  sib: any
  written: any
  scenario: any
  qa: any
  plan: any
  deepResearch?: DeepResearchData | null
  report_id?: string
  research_id?: number | null
  title?: string
  query?: string
  summary?: string
  report_text?: string
  written_report?: string
  deep_research_report?: string
  image_url?: string
  compiled_views?: any
  views?: any
  author?: {
    name?: string
    email?: string
    avatar_url?: string | null
  } | null
  author_name?: string | null
  author_email?: string | null
  author_avatar?: string | null
  is_public?: boolean
  saved_by_user?: boolean
  [key: string]: any
}

export interface IntelligenceReportStudy extends PodcastSurfaceState {
  id: string
  query: string
  title?: string
  summary?: string
  category?: string
  model?: 'chatgpt' | 'gemini' | 'claude'
  status?: 'pending' | 'queued' | 'in_progress' | 'interrupted' | 'podcast_pending' | 'completed' | 'failed' | 'planning' | 'awaiting_confirmation' | 'cancelled'
  createdAt?: Date | string
  created_at?: string
  completedAt?: Date | string
  completed_at?: string
  image_url?: string
  isSaved?: boolean
  is_saved?: boolean
  isPublic?: boolean
  is_public?: boolean
  isIntelligenceReport?: boolean
  is_intelligence_report?: boolean
  intelligenceReportId?: string
  intelligence_report_id?: string
  researchId?: number
  research_id?: number
  author?: {
    id?: string
    name: string
    email?: string
    avatar_url?: string | null
  }
}

type CurrentUser = {
  name: string
  email: string
  avatar_url?: string | null
} | null | undefined

type IntelligenceReportCockpitProps = {
  reportData: IntelligenceReportData
  title: string
  onBack: () => void
  viewMode: ViewMode
  hasFloatingAudio?: boolean
  study?: IntelligenceReportStudy | null
  onShowToast: (toast: Omit<ToastMessage, 'id'>) => void
  onRequestDownload?: () => void
  onPrivacyToggle?: (isPublic: boolean) => Promise<boolean>
  onNavigateToProfile?: (email: string) => void
  currentUser?: CurrentUser
}

function normalizeDate(value?: Date | string | null): Date | undefined {
  if (!value) return undefined
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value
  }
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? undefined : parsed
}

function toIsoString(value?: Date | string | null): string | undefined {
  return normalizeDate(value)?.toISOString()
}

function deriveIntelligenceReportId(
  study?: Partial<IntelligenceReportStudy>,
  reportData?: Partial<IntelligenceReportData>
): string | undefined {
  if (study?.intelligenceReportId) return study.intelligenceReportId
  if (study?.intelligence_report_id) return study.intelligence_report_id
  if (reportData?.report_id) return reportData.report_id
  const rawId = study?.id
  if (rawId && String(rawId).startsWith('ia-')) {
    return String(rawId).replace('ia-', '')
  }
  return undefined
}

export function normalizeIntelligenceReportData(reportData: any): IntelligenceReportData {
  const deepResearchPayload = reportData.deepResearch ?? reportData.deep_research ?? null

  return {
    ...reportData,
    deepResearch: deepResearchPayload
      ? {
          text: deepResearchPayload.text || deepResearchPayload.content || '',
          sources: deepResearchPayload.sources || [],
          citations: deepResearchPayload.citations || [],
          status: deepResearchPayload.status,
          provider: deepResearchPayload.provider,
        }
      : null,
  }
}

export function buildIntelligenceReportStudy(
  baseStudy: Partial<IntelligenceReportStudy>,
  reportData: IntelligenceReportData
): IntelligenceReportStudy {
  const intelligenceReportId = deriveIntelligenceReportId(baseStudy, reportData)
  const hydratedPodcastState = hydratePodcastSurfaceState(baseStudy, reportData)
  const author =
    reportData.author_name
      ? {
          name: reportData.author_name,
          email: reportData.author_email ?? undefined,
          avatar_url: reportData.author_avatar ?? undefined,
        }
      : reportData.author?.name
        ? {
            name: reportData.author.name,
            email: reportData.author.email ?? undefined,
            avatar_url: reportData.author.avatar_url ?? undefined,
          }
        : baseStudy.author
          ? {
              ...baseStudy.author,
              avatar_url: baseStudy.author.avatar_url ?? undefined,
            }
          : undefined

  return {
    id: String(baseStudy.id ?? (intelligenceReportId ? `ia-${intelligenceReportId}` : 'intelligence-report')),
    query: reportData.query || baseStudy.query || '',
    title: reportData.title || baseStudy.title || baseStudy.query || 'Intelligence Report',
    summary: reportData.summary || baseStudy.summary || '',
    category: baseStudy.category,
    model: baseStudy.model,
    status: baseStudy.status || reportData.status,
    createdAt: normalizeDate(baseStudy.createdAt ?? baseStudy.created_at ?? reportData.created_at),
    completedAt: normalizeDate(baseStudy.completedAt ?? baseStudy.completed_at),
    image_url: reportData.image_url || baseStudy.image_url,
    isSaved: reportData.saved_by_user ?? baseStudy.isSaved ?? baseStudy.is_saved,
    isPublic: reportData.is_public ?? baseStudy.isPublic ?? baseStudy.is_public,
    isIntelligenceReport: true,
    intelligenceReportId,
    researchId: reportData.research_id ?? baseStudy.researchId ?? baseStudy.research_id,
    author,
    ...hydratedPodcastState,
  }
}

export function IntelligenceReportCockpit({
  reportData,
  title,
  onBack,
  viewMode,
  hasFloatingAudio = false,
  study,
  onShowToast,
  onRequestDownload,
  onPrivacyToggle,
  onNavigateToProfile,
  currentUser,
}: IntelligenceReportCockpitProps) {
  const normalizedReportData = useMemo(
    () => normalizeIntelligenceReportData(reportData),
    [reportData]
  )
  const [podcastOverride, setPodcastOverride] = useState<Partial<IntelligenceReportStudy>>({})
  const mergedStudy = useMemo(
    () => (study ? { ...study, ...podcastOverride } : null),
    [podcastOverride, study]
  )

  const { cig, views, persona } = useMemo(() => {
    try {
      const cig = normalizedReportData.cig || {}
      const cigMetadata = cig.metadata || {}

      const scenarioAxes = (() => {
        const scenarioPackage = normalizedReportData.scenario
        if (!scenarioPackage?.axis) return cig.scenario_axes ?? []

        const axis = scenarioPackage.axis
        return [{
          axis_id: axis.axis_id || 'default',
          label: axis.label || 'Scenario Analysis',
          description: axis.description,
          states: (axis.states || []).map((state: any) => ({
            state_id: state.state_id,
            label: state.label,
            short_description: state.short_description || state.description,
            is_default: state.is_default,
            probability_band: state.probability_band,
            triggers: (state.triggers || []).map((trigger: any) => ({
              trigger_id: trigger.trigger_id || trigger.id,
              description: trigger.description,
              observability: trigger.observability,
              confidence: trigger.confidence,
            })),
            assumptions: (state.assumptions || []).map((assumption: any) => ({
              assumption_id: assumption.assumption_id || assumption.id,
              text: assumption.text,
              rationale: assumption.rationale,
            })),
            metric_impacts: state.metric_impacts || [],
          })),
          triggers: (scenarioPackage.global_triggers || axis.triggers || []).map((trigger: any) => ({
            id: trigger.trigger_id || trigger.id || `trigger-${Math.random().toString(36).substr(2, 9)}`,
            label: trigger.label || trigger.description,
            definition: trigger.definition || trigger.description,
            indicator_type: trigger.indicator_type,
            maps_to_state_transitions: trigger.maps_to_state_transitions,
            confidence: trigger.confidence,
          })),
          assumptions: (axis.assumptions || []).map((assumption: any) => ({
            id: assumption.assumption_id || assumption.id,
            text: assumption.text,
            applies_to_state_id: assumption.applies_to_state_id,
          })),
        }]
      })()

      const enhancedCig: CIG = {
        ...cig,
        metadata: {
          ...cigMetadata,
          schema_version: cigMetadata.schema_version || '2.2',
          report_id: cigMetadata.report_id || 'intelligence-report',
          title: (() => {
            const sibTitle = normalizedReportData?.sib?.research_title
            if (sibTitle && sibTitle.length > 10 && sibTitle !== title) return sibTitle
            if (cigMetadata.title && cigMetadata.title.length > 10 && cigMetadata.title !== title) return cigMetadata.title
            return cigMetadata.title || title
          })(),
        },
        sources: cig.sources ?? [],
        claims: cig.claims ?? [],
        entities: cig.entities ?? [],
        metrics: normalizeLegacyMetrics(cig.metrics as any),
        relationships: cig.relationships ?? [],
        figures: cig.figures ?? [],
        datasets: cig.datasets ?? [],
        watchlist: cig.watchlist ?? [],
        key_findings: cig.key_findings ?? [],
        executive_summary: cig.executive_summary ?? [],
        bluf: cig.bluf ?? title,
        report_sections: cig.report_sections ?? [],
        scenario_axes: scenarioAxes.length > 0 ? scenarioAxes : cig.scenario_axes,
      }

      const selectedPersona =
        DEFAULT_PERSONAS.find((candidate) => candidate.persona_id === 'general_exec') ||
        DEFAULT_PERSONAS[0]

      const compiledViews = compileViews(
        enhancedCig,
        selectedPersona,
        normalizedReportData.sib,
        normalizedReportData.deepResearch ||
          normalizedReportData.deep_research ||
          ((normalizedReportData.written_report || normalizedReportData.deep_research_report || normalizedReportData.report_text)
            ? {
                text:
                  normalizedReportData.written_report ||
                  normalizedReportData.deep_research_report ||
                  normalizedReportData.report_text,
              }
            : undefined)
      )

      return { cig: enhancedCig, views: compiledViews, persona: selectedPersona }
    } catch (error) {
      console.error('Error compiling cockpit views:', error)
      return { cig: null, views: null, persona: null }
    }
  }, [normalizedReportData, title])

  const heroImageUrl =
    mergedStudy?.image_url ||
    normalizedReportData.image_url ||
    (cig as any)?.metadata?.hero_image_url ||
    (views?.cockpit?.hero_viz as any)?.data?.image_url

  const updatePodcastState = useCallback((podcastInfo: any) => {
    setPodcastOverride({
      ...hydratePodcastSurfaceState(
        {
          podcast_status: mergedStudy?.podcast_status,
          podcast_audio_ready: mergedStudy?.podcast_audio_ready,
          podcast_duration_seconds: mergedStudy?.podcast_duration_seconds,
          podcast_error_message: mergedStudy?.podcast_error_message,
        },
        {
          podcast_status: podcastInfo.status,
          podcast_audio_ready: Boolean(podcastInfo.audio_url),
          podcast_duration_seconds: podcastInfo.duration_seconds,
          podcast_error_message: podcastInfo.error_message ?? undefined,
          podcast: podcastInfo,
        }
      ),
    })
  }, [
    mergedStudy?.podcast_audio_ready,
    mergedStudy?.podcast_duration_seconds,
    mergedStudy?.podcast_error_message,
    mergedStudy?.podcast_status,
  ])

  const podcastAction = mergedStudy
    ? getPodcastActionState({
        id: String(mergedStudy.id),
        is_intelligence_report: Boolean(mergedStudy.isIntelligenceReport),
        intelligence_report_id: mergedStudy.intelligenceReportId,
        podcast_status: mergedStudy.podcast_status ?? undefined,
        podcast_audio_ready: mergedStudy.podcast_audio_ready ?? undefined,
        podcast_error_message: mergedStudy.podcast_error_message ?? undefined,
      })
    : null

  const handleListen = useCallback(async () => {
    const reportId = mergedStudy?.intelligenceReportId
    if (!reportId) {
      onShowToast({
        type: 'warning',
        title: 'Podcast unavailable',
        description: 'This report is still syncing its playback metadata.',
      })
      return
    }
    if (podcastAction?.disabled) {
      return
    }

    try {
      let pendingToastShown = false
      const podcastInfo = await resolveIntelligencePodcastPlayback(reportId, {
        onPending: (pending) => {
          updatePodcastState(pending)
          if (pendingToastShown) return
          pendingToastShown = true
          onShowToast({
            type: 'info',
            title: 'Preparing Podcast',
            description: 'Podcast generation has started. Playback will begin as soon as audio is ready.',
          })
        },
      })
      updatePodcastState(podcastInfo)

      if (!podcastInfo.audio_url) {
        onShowToast({
          type: podcastInfo.status === 'failed' ? 'error' : 'warning',
          title: podcastInfo.status === 'failed' ? 'Podcast failed' : 'Podcast still processing',
          description:
            podcastInfo.error_message ||
            'The podcast is still being generated. Please try again in a moment.',
        })
        return
      }

      dispatchPodcastPlayback(
        {
          id: String(mergedStudy?.id || reportId),
          title: mergedStudy?.title || title,
          description: mergedStudy?.query,
          summary: mergedStudy?.summary || '',
          category: mergedStudy?.category || 'General',
          trending_percentage: 0,
          engagement_count: 0,
          sources: [],
          created_at: toIsoString(mergedStudy?.createdAt) || new Date().toISOString(),
          tags: [],
          image_url: heroImageUrl,
          is_intelligence_report: true,
          intelligence_report_id: reportId,
        },
        podcastInfo
      )
    } catch (error) {
      console.error('Failed to start cockpit podcast playback:', error)
      onShowToast({
        type: 'error',
        title: 'Podcast failed',
        description: error instanceof Error ? error.message : 'Unable to load the podcast right now.',
      })
    }
  }, [heroImageUrl, mergedStudy, onShowToast, podcastAction?.disabled, title, updatePodcastState])

  const authorInfo = mergedStudy?.author || (currentUser ? {
    name: currentUser.name,
    email: currentUser.email,
    avatar_url: currentUser.avatar_url,
  } : undefined)

  const studyMeta = mergedStudy
    ? {
        id: mergedStudy.id,
        intelligenceReportId: mergedStudy.intelligenceReportId,
        researchId: mergedStudy.researchId,
        isSaved: mergedStudy.isSaved,
        isPublic: mergedStudy.isPublic,
        category: mergedStudy.category,
        model: mergedStudy.model,
        createdAt: toIsoString(mergedStudy.createdAt),
        completedAt: toIsoString(mergedStudy.completedAt),
        query: mergedStudy.query,
        researcher: authorInfo?.name,
        researcherEmail: authorInfo?.email,
        researcherAvatarUrl: authorInfo?.avatar_url || undefined,
      }
    : undefined

  if (!cig || !views || !persona) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center p-8">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Unable to Load Executive Overview
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            The report data could not be compiled for display.
          </p>
          <button
            onClick={onBack}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <CockpitRuntime
      cig={cig}
      views={views}
      persona={persona}
      onBack={onBack}
      onListen={podcastAction?.visible ? handleListen : undefined}
      listenLabel={podcastAction?.label}
      listenDisabled={podcastAction?.disabled}
      listenLoading={podcastAction?.showSpinner}
      viewMode={viewMode === 'mobile' ? 'mobile' : 'desktop'}
      hasFloatingAudio={hasFloatingAudio}
      studyMeta={studyMeta}
      heroImageUrl={heroImageUrl}
      onRequestDownload={onRequestDownload}
      onPrivacyToggle={onPrivacyToggle}
      onNavigateToProfile={onNavigateToProfile}
    />
  )
}

export default IntelligenceReportCockpit
